/**
 * 业务数据翻译：后端商品/港口/包装/询盘备注等数据多为中文原文，这里按“已知词条”把中文片段换成目标语言。
 * - 全站唯一一份对照表（原先散落在 catalog / 询盘列表 / 询盘进度 / 询价对话 四处）；
 * - 只翻译已知词条，买家/顾问的自由文本保持原样；
 * - 韩文优先用韩文词条，整句仍残留中文时回退英文字段或英文词条。
 */
import { DICT, type Lang } from './dict';
import { KO } from './ko';
import { getCurrentLang } from './index';

type Term = { en: string; ko: string };

const TERMS: Record<string, Term> = {
  // ---- 商品 / 品种 / 规格 ----
  '保鲜姜': { en: 'Fresh Ginger', ko: '신선 생강' },
  '沃柑': { en: 'Wogan Mandarin', ko: '워간 만다린' },
  '柠檬': { en: 'Lemon', ko: '레몬' },
  '柚子': { en: 'Pomelo', ko: '포멜로' },
  '梨': { en: 'Pear', ko: '배' },
  '番茄': { en: 'Tomato', ko: '토마토' },
  '黄瓜': { en: 'Cucumber', ko: '오이' },
  '西葫芦': { en: 'Zucchini', ko: '주키니' },
  '彩椒': { en: 'Bell Pepper', ko: '파프리카' },
  '贝贝南瓜': { en: 'Kabocha Squash', ko: '미니 단호박' },
  '球生菜': { en: 'Iceberg Lettuce', ko: '양상추' },
  '白萝卜': { en: 'Daikon Radish', ko: '무' },
  '甘蓝': { en: 'Round Cabbage', ko: '양배추' },
  '纯白蒜': { en: 'Pure White Garlic ', ko: '순백 마늘 ' },
  '红蒜': { en: 'Normal White Garlic ', ko: '일반 백마늘 ' },
  '独头蒜': { en: 'Solo Garlic ', ko: '외톨 마늘 ' },
  '大包装': { en: ' bulk pack ', ko: ' 대포장 ' },
  '小包装': { en: ' small pack ', ko: ' 소포장 ' },
  '保鲜': { en: 'Fresh', ko: '신선' },
  '风干': { en: 'Air-dried', ko: '건조' },
  '以上': { en: '+', ko: '+' },
  '尤力克': { en: 'Eureka', ko: '유레카' },
  '红柚': { en: 'Red-flesh Pomelo', ko: '적육 포멜로' },
  '白柚': { en: 'White-flesh Pomelo', ko: '백육 포멜로' },
  '皇冠梨': { en: 'Huangguan Pear', ko: '황관배' },
  '富士': { en: 'Fuji', ko: '후지' },
  '硬粉': { en: 'Pink', ko: '핑크' },
  '大红': { en: 'Red', ko: '레드' },
  '密刺大黄瓜': { en: 'Spiny Cucumber', ko: '가시오이' },
  '无刺小黄瓜': { en: 'Mini Smooth Cucumber', ko: '미니 오이' },
  '红椒': { en: 'Red Bell Pepper', ko: '빨간 파프리카' },
  '黄椒': { en: 'Yellow Bell Pepper', ko: '노란 파프리카' },
  '青椒': { en: 'Green Bell Pepper', ko: '피망' },
  '惠和一号': { en: 'Huihe No.1', ko: '후이허 1호' },
  '黄心': { en: 'Yellow-heart', ko: '노란 속' },
  '去皮黄洋葱': { en: 'Peeled Yellow Onion', ko: '깐 양파' },
  '黄皮': { en: 'Yellow Skin', ko: '노란 껍질' },
  '娃娃菜': { en: 'Baby cabbage', ko: '미니 배추' },
  '大白菜': { en: 'Napa cabbage', ko: '배추' },
  '白菜': { en: 'Chinese cabbage', ko: '배추' },
  '西兰花': { en: 'Broccoli', ko: '브로콜리' },
  '大姜': { en: 'Ginger', ko: '생강' },
  '生姜': { en: 'Ginger', ko: '생강' },
  '大蒜': { en: 'Garlic', ko: '마늘' },
  '洋葱': { en: 'Onion', ko: '양파' },
  '胡萝卜丝': { en: 'Carrot', ko: '당근' },
  '胡萝卜': { en: 'Carrot', ko: '당근' },
  '马铃薯': { en: 'Potato', ko: '감자' },
  '土豆': { en: 'Potato', ko: '감자' },
  '苹果': { en: 'Apple', ko: '사과' },
  '纯白': { en: 'Pure White', ko: '순백' },
  '紫皮': { en: 'Purple Skin', ko: '자색 껍질' },
  '白皮': { en: 'White Skin', ko: '흰 껍질' },
  '红薯': { en: 'Sweet potato', ko: '고구마' },
  '南瓜': { en: 'Pumpkin', ko: '호박' },
  // ---- 状态 / 单证 / 付款 ----
  '处理中': { en: 'Processing', ko: '처리 중' },
  '定损中': { en: 'Assessing', ko: '손해 사정 중' },
  '审批中': { en: 'Under review', ko: '검토 중' },
  '待赔付': { en: 'Pending payout', ko: '보상 대기' },
  '已结案-赔付': { en: 'Settled — paid', ko: '종결 — 보상 완료' },
  '已结案-拒赔': { en: 'Settled — rejected', ko: '종결 — 보상 거절' },
  '已核销': { en: 'Verified', ko: '확인 완료' },
  '待核销': { en: 'Pending verification', ko: '확인 대기' },
  '未提交': { en: 'Not submitted', ko: '미제출' },
  '温度报警': { en: 'Temperature alert', ko: '온도 경보' },
  '已完成': { en: 'Completed', ko: '완료' },
  'IoT离线': { en: 'IoT offline', ko: 'IoT 오프라인' },
  '四证待校': { en: 'Documents pending verification', ko: '서류 검증 대기' },
  '节点超时': { en: 'Node overdue', ko: '단계 지연' },
  '正常': { en: 'Normal', ko: '정상' },
  '待签约': { en: 'Pending signature', ko: '계약 대기' },
  '定金': { en: 'Deposit', ko: '계약금' },
  '尾款': { en: 'Balance', ko: '잔금' },
  // ---- 地点 ----
  '阿拉伯联合酋长国': { en: 'United Arab Emirates', ko: '아랍에미리트' },
  '阿联酋': { en: 'United Arab Emirates', ko: '아랍에미리트' },
  '迪拜': { en: 'Dubai', ko: '두바이' },
  '中国': { en: 'China', ko: '중국' },
  '沙特阿拉伯': { en: 'Saudi Arabia', ko: '사우디아라비아' },
  '卡塔尔': { en: 'Qatar', ko: '카타르' },
  '科威特': { en: 'Kuwait', ko: '쿠웨이트' },
  '阿曼': { en: 'Oman', ko: '오만' },
  '巴林': { en: 'Bahrain', ko: '바레인' },
  '伊拉克': { en: 'Iraq', ko: '이라크' },
  '土耳其': { en: 'Türkiye', ko: '튀르키예' },
  '埃及': { en: 'Egypt', ko: '이집트' },
  '摩洛哥': { en: 'Morocco', ko: '모로코' },
  '马来西亚': { en: 'Malaysia', ko: '말레이시아' },
  '印度尼西亚': { en: 'Indonesia', ko: '인도네시아' },
  '泰国': { en: 'Thailand', ko: '태국' },
  '越南': { en: 'Vietnam', ko: '베트남' },
  '菲律宾': { en: 'Philippines', ko: '필리핀' },
  '新加坡': { en: 'Singapore', ko: '싱가포르' },
  '日本': { en: 'Japan', ko: '일본' },
  '韩国': { en: 'South Korea', ko: '대한민국' },
  '印度': { en: 'India', ko: '인도' },
  '巴基斯坦': { en: 'Pakistan', ko: '파키스탄' },
  '孟加拉国': { en: 'Bangladesh', ko: '방글라데시' },
  '斯里兰卡': { en: 'Sri Lanka', ko: '스리랑카' },
  '荷兰': { en: 'Netherlands', ko: '네덜란드' },
  '德国': { en: 'Germany', ko: '독일' },
  '英国': { en: 'United Kingdom', ko: '영국' },
  '法国': { en: 'France', ko: '프랑스' },
  '西班牙': { en: 'Spain', ko: '스페인' },
  '意大利': { en: 'Italy', ko: '이탈리아' },
  '俄罗斯': { en: 'Russia', ko: '러시아' },
  '美国': { en: 'United States', ko: '미국' },
  '加拿大': { en: 'Canada', ko: '캐나다' },
  '澳大利亚': { en: 'Australia', ko: '호주' },
  '新西兰': { en: 'New Zealand', ko: '뉴질랜드' },
  '巴西': { en: 'Brazil', ko: '브라질' },
  '南非': { en: 'South Africa', ko: '남아프리카공화국' },
  '尼日利亚': { en: 'Nigeria', ko: '나이지리아' },
  '肯尼亚': { en: 'Kenya', ko: '케냐' },
  '坦桑尼亚': { en: 'Tanzania', ko: '탄자니아' },
  '安哥拉': { en: 'Angola', ko: '앙골라' },
  '加纳': { en: 'Ghana', ko: '가나' },
  '杰贝阿里港': { en: 'Jebel Ali Port', ko: '제벨알리항' },
  '杰贝阿里': { en: 'Jebel Ali', ko: '제벨알리' },
  // ---- 询盘备注里的字段名（询盘记录以中文保存，展示时翻译） ----
  '贸易术语：': { en: 'Trade term: ', ko: '무역 조건: ' },
  '贸易条款：': { en: 'Trade term: ', ko: '무역 조건: ' },
  '贸易术语偏好：': { en: 'Trade term preference: ', ko: '선호 무역 조건: ' },
  '每箱净重：': { en: 'Carton net weight: ', ko: '박스당 순중량: ' },
  '内包装混装方案': { en: 'Mixed inner-pack plan', ko: '내포장 혼합 구성' },
  '内包装分别报价方案': { en: 'Separate quotes per inner pack', ko: '내포장별 개별 견적' },
  '内包装候选方案': { en: 'Inner-pack options', ko: '내포장 후보안' },
  '内包装：': { en: 'Inner packaging: ', ko: '내포장: ' },
  '内袋规格：': { en: 'Inner pack: ', ko: '내포장 규격: ' },
  '外包装：': { en: 'Outer packaging: ', ko: '외포장: ' },
  '定制/补充要求：': { en: 'Custom or additional requirements: ', ko: '맞춤/추가 요청: ' },
  '品种/规格：': { en: 'Variety/specification: ', ko: '품종/규격: ' },
  '期望到货：': { en: 'Need by: ', ko: '희망 도착일: ' },
  '期望出运：': { en: 'Ship by: ', ko: '희망 선적일: ' },
  '指定装运港：': { en: 'Port of loading: ', ko: '지정 선적항: ' },
  '交期偏好：': { en: 'Delivery preference: ', ko: '납기 선호: ' },
  '运输偏好：': { en: 'Transport preference: ', ko: '운송 선호: ' },
  '分配：': { en: 'Allocation: ', ko: '배분: ' },
  // ---- 包装 ----
  '直接装外箱（无内包装）': { en: 'Packed directly in the outer carton', ko: '외박스에 바로 포장(내포장 없음)' },
  '无内包装': { en: 'No inner packaging', ko: '내포장 없음' },
  '单个大包装': { en: 'one large bag', ko: '대포장 1개' },
  '箱内分装小袋': { en: 'smaller bags inside each carton', ko: '박스 안 소포장' },
  '瓦楞纸箱（常规出口）': { en: 'corrugated export carton (standard)', ko: '골판지 박스(일반 수출용)' },
  '瓦楞纸箱': { en: 'corrugated carton', ko: '골판지 박스' },
  '泡沫箱（直接装箱）': { en: 'foam box (direct pack)', ko: '스티로폼 박스(직접 포장)' },
  '泡沫箱': { en: 'foam box', ko: '스티로폼 박스' },
  '塑料筐': { en: 'plastic crate', ko: '플라스틱 상자' },
  '钙塑箱': { en: 'calcium plastic box', ko: '칼슘 플라스틱 박스' },
  '网袋': { en: 'mesh bag', ko: '망 포대' },
  '纸箱': { en: 'carton', ko: '종이 박스' },
  '按常规外包装，无定制要求': { en: 'Standard outer packaging; no custom requirements', ko: '일반 외포장, 맞춤 요청 없음' },
  '无定制要求': { en: 'no customization', ko: '맞춤 요청 없음' },
  '请平台按商品和运输方式推荐': { en: 'Platform to recommend suitable packaging based on the product and shipping method', ko: '상품과 운송 방식에 맞게 플랫폼에서 추천' },
  // ---- 交期 / 运输偏好 ----
  '尽量快，优先考虑时效': { en: 'Prioritize faster delivery', ko: '최대한 빠르게, 납기 우선' },
  '尽量快，优先时效': { en: 'Prioritize faster delivery', ko: '최대한 빠르게, 납기 우선' },
  '费用优先': { en: 'Prioritize lower cost', ko: '비용 우선' },
  '尽量少中转': { en: 'Minimize transfers', ko: '환적 최소화' },
  '暂无偏好，由平台安排': { en: 'No preference; please arrange', ko: '선호 없음, 플랫폼에 맡김' },
  '不确定，请给我多种方案': { en: 'Please provide multiple options', ko: '여러 안을 제시해 주세요' },
  '请提供多种方案': { en: 'Please provide multiple options', ko: '여러 안을 제시해 주세요' },
  '尽快安排': { en: 'As soon as possible', ko: '최대한 빨리' },
  '时间灵活': { en: 'Timing is flexible', ko: '일정 유연' },
  '品种/规格暂不确定': { en: 'Variety/specification pending', ko: '품종/규격 미정' },
  // ---- 人员 ----
  '蓝宝': { en: 'Blu', ko: '블루' },
  '顾问': { en: 'Advisor', ko: '담당자' },
  '买家': { en: 'Buyer', ko: '바이어' },
};

const HAN = /[㐀-鿿]/;
const SORTED = Object.keys(TERMS).sort((a, b) => b.length - a.length);

type Foreign = Exclude<Lang, 'zh'>;

function replaceTerms(value: string, lang: Foreign): string {
  let text = value;
  for (const source of SORTED) {
    if (text.includes(source)) text = text.split(source).join(TERMS[source][lang]);
  }
  if (lang === 'en') {
    text = text
      .replace(/(\d+)\s*件\s*mesh bags?/gi, (_, count: string) => `${count} mesh bag${count === '1' ? '' : 's'}`)
      .replace(/(\d)\s*(粒|头)/g, '$1 pcs')
      .replace(/(^|\s)大(?=\s|$)/g, '$1Large').replace(/(^|\s)小(?=\s|$)/g, '$1Small');
  } else {
    text = text
      .replace(/(\d+)\s*件\s*/g, '$1개 ')
      .replace(/(\d)\s*(粒|头)/g, '$1개')
      .replace(/(^|\s)大(?=\s|$)/g, '$1대').replace(/(^|\s)小(?=\s|$)/g, '$1소');
  }
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * 把中文数据里的已知词条翻成目标语言。保持原有约定：不传语言时固定输出英文
 * （历史调用与测试依赖这一点）；页面上「跟随当前界面语言」请用 localizeText。
 * 韩文翻译后仍残留中文时，回退到英文词条（英文比中文对韩国买家更友好）。
 */
export function catalogText(value: string, lang: Lang = 'en'): string {
  if (!value || lang === 'zh') return value ?? '';
  if (lang === 'en') return replaceTerms(value, 'en');
  const ko = replaceTerms(value, 'ko');
  if (!HAN.test(ko)) return ko;
  const en = replaceTerms(value, 'en');
  return HAN.test(en) ? ko : en;
}

/** 历史数据里以英文保存的整段词条（如目的国 United Arab Emirates）：韩文界面换成韩文 */
let enToKo: Map<string, string> | null = null;
export function koFromEnglish(value: string): string | undefined {
  if (!enToKo) {
    enToKo = new Map();
    for (const term of Object.values(TERMS)) {
      const en = term.en.trim().toLowerCase();
      if (en.length > 2 && !enToKo.has(en)) enToKo.set(en, term.ko.trim());
    }
    // 英文界面写入询盘的选项短语（journey.* 等），同样按整段换成韩文
    for (const [key, entry] of Object.entries(DICT)) {
      if (!/^(journey|journeyCopy|inquiryText|inquiry)\./.test(key) || /\{\w+\}/.test(entry.en)) continue;
      const en = entry.en.trim().toLowerCase();
      const ko = (KO as Record<string, string | undefined>)[key];
      if (ko && en.length > 2 && !enToKo.has(en)) enToKo.set(en, ko.trim());
    }
  }
  return enToKo.get(value.trim().toLowerCase());
}

/** 当前语言下展示数据文本（中文界面原样展示） */
export const localizeText = (value: string | null | undefined, lang: Lang = getCurrentLang()): string => catalogText(value ?? '', lang);

export function skuVarietyLabel(lang: Lang, source?: string, english?: string): string {
  return lang !== 'zh' && /大包装|小包装/.test(source || '') ? catalogText(source || '', lang) : catalogLabel(lang, source, english);
}

/** 商品/规格等后端字段：中文取原文，英文优先英文字段，韩文优先韩文词条、其次英文字段 */
export function catalogLabel(lang: Lang, source?: string | null, english?: string | null): string {
  if (lang === 'zh') return source ?? '';
  const en = english?.trim();
  if (lang === 'ko') {
    const ko = replaceTerms(source || '', 'ko');
    if (ko && !HAN.test(ko)) return ko;
    if (en) return en;
    return catalogText(source || '', 'ko');
  }
  return en || catalogText(source || '', 'en');
}

const PACKAGING_UNITS: Record<Foreign, Array<[RegExp, string]>> = {
  en: [[/公斤/g, 'kg'], [/千克/g, 'kg'], [/吨/g, 't'], [/箱/g, 'carton'], [/袋/g, 'bag']],
  ko: [[/公斤/g, 'kg'], [/千克/g, 'kg'], [/吨/g, 't'], [/箱/g, '박스'], [/袋/g, '봉지']],
};

export function packagingLabel(lang: Lang, value?: string | null): string {
  if (lang === 'zh') return value ?? '';
  return PACKAGING_UNITS[lang].reduce((text, [pattern, unit]) => text.replace(pattern, unit), value ?? '');
}

const NODE_LABELS: Record<string, Term> = {
  order: { en: 'Order', ko: '주문' },
  produce: { en: 'Production', ko: '생산' },
  pack: { en: 'Packing', ko: '포장' },
  load: { en: 'Container loading', ko: '컨테이너 적재' },
  export_customs: { en: 'Export customs clearance', ko: '수출 통관' },
  ship: { en: 'Ocean shipping', ko: '해상 운송' },
  arrive: { en: 'Arrival', ko: '도착' },
  import_customs: { en: 'Import customs clearance', ko: '수입 통관' },
  warehouse: { en: 'Overseas warehouse', ko: '해외 창고' },
  deliver: { en: 'Delivery', ko: '배송' },
};

export function fulfillmentNodeLabel(lang: Lang, code: string, fallback: string): string {
  if (lang === 'zh') return fallback;
  return NODE_LABELS[code]?.[lang] || fallback.replace(/[㐀-鿿]+/g, '').replace(/^[\s·:：-]+|[\s·:：-]+$/g, '').trim() || code;
}

/**
 * 写进询盘记录（后台人员查看）的文本：韩文界面里由按钮/选项生成的固定韩文短语，
 * 反查回中文原文；买家手打的自由文本保持原样。中/英文界面原样返回（与既有行为一致）。
 */
let recordPairs: Array<[string, string]> | null = null;
export function recordText(value: string | null | undefined, lang: Lang = getCurrentLang()): string {
  const text = value ?? '';
  if (!text || lang !== 'ko') return text;
  if (!recordPairs) {
    const pairs = new Map<string, string>();
    for (const [source, term] of Object.entries(TERMS)) {
      // 带尾随空格的字段名（如「외포장: 」）连同空格一起换回，避免中文里多出空格
      for (const ko of [term.ko, term.ko.trim()]) if (ko.trim().length >= 2 && !pairs.has(ko)) pairs.set(ko, source);
    }
    for (const [key, ko] of Object.entries(KO as Record<string, string | undefined>)) {
      const zh = DICT[key]?.zh;
      // 只反查不含占位符的整句/短语，避免误伤自由文本
      if (!ko || !zh || ko.length < 2 || /\{\w+\}/.test(ko) || !/^(journey|journeyCopy|inquiryText)\./.test(key)) continue;
      if (!pairs.has(ko.trim())) pairs.set(ko.trim(), zh);
    }
    recordPairs = [...pairs.entries()].sort(([a], [b]) => b.length - a.length);
  }
  return recordPairs.reduce((out, [ko, zh]) => (out.includes(ko) ? out.split(ko).join(zh) : out), text.replace(/(\d+)개 망 포대/g, '$1 件网袋'));
}
